import {
  BrowserDataCacheCapabilities,
  BrowserDataCacheQuery,
  BrowserDataCacheQueryResult,
  BrowserDataCacheSyncJob,
  BrowserDataCacheSyncOptions,
} from './types';

export interface BrowserDataCacheRuntime {
  readonly capabilities: BrowserDataCacheCapabilities;
  startSync(sourceId: string, options?: BrowserDataCacheSyncOptions): BrowserDataCacheSyncJob;
  query<TRow = unknown>(query: BrowserDataCacheQuery): Promise<BrowserDataCacheQueryResult<TRow>>;
  upsertRows(table: string, rows: unknown[]): Promise<void>;
}

export interface BrowserDataCacheRuntimeProbeResult {
  ok: boolean;
  reason?: string;
  capabilities?: BrowserDataCacheCapabilities;
}

export interface BrowserDataCacheRuntimeCandidate {
  id: string;
  probe(): Promise<BrowserDataCacheRuntimeProbeResult> | BrowserDataCacheRuntimeProbeResult;
  create(): Promise<BrowserDataCacheRuntime> | BrowserDataCacheRuntime;
}

export interface BrowserDataCacheRuntimeSelection {
  runtime: BrowserDataCacheRuntime;
  selectedId: string;
  probes: Array<{
    id: string;
    result: BrowserDataCacheRuntimeProbeResult;
  }>;
}

export async function selectBrowserDataCacheRuntime(
  candidates: BrowserDataCacheRuntimeCandidate[],
): Promise<BrowserDataCacheRuntimeSelection> {
  const probes: BrowserDataCacheRuntimeSelection['probes'] = [];
  for (const candidate of candidates || []) {
    const result = await candidate.probe();
    probes.push({ id: candidate.id, result });
    if (!result.ok) {
      continue;
    }
    return {
      runtime: await candidate.create(),
      selectedId: candidate.id,
      probes,
    };
  }
  const reasons = probes.map((probe) => `${probe.id}: ${probe.result.reason || 'probe failed'}`).join('; ');
  throw new Error(`No browser data cache runtime available${reasons ? ` (${reasons})` : ''}`);
}
